import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, KeyRound, Lock, ArrowLeft, Fingerprint } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const steps = ['email', 'otp', 'reset'];

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email');
  const [tenantId, setTenantId] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const stepIndex = steps.indexOf(step);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!tenantId.trim() || !email.trim()) {
      toast.error('Please fill in all fields.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/forgot-password`, { email: email.trim(), tenantId: tenantId.trim() });
      toast.success('OTP sent! Check your email.');
      setStep('otp');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim()) { toast.error('Please enter the OTP.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/verify-otp`, { email: email.trim(), tenantId: tenantId.trim(), otp: otp.trim() });
      toast.success('OTP verified!');
      setStep('reset');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Invalid OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword) { toast.error('Please enter a new password.'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match.'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/auth/reset-password`, {
        email: email.trim(),
        tenantId: tenantId.trim(),
        otp: otp.trim(),
        newPassword,
      });
      toast.success('Password reset successfully!');
      navigate('/login');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 'otp') { setStep('email'); setOtp(''); }
    else if (step === 'reset') { setStep('otp'); }
    else navigate('/login');
  };

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center bg-[#fafafa] py-12 px-4 sm:px-6 lg:px-8">
      {/* Background blobs */}
      <div className="absolute -top-24 -left-24 w-[38rem] h-[38rem] rounded-full bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.25),_transparent_60%)] blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-[38rem] h-[38rem] rounded-full bg-[radial-gradient(ellipse_at_center,_rgba(16,185,129,0.25),_transparent_60%)] blur-3xl" />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <Fingerprint className="w-[46rem] h-[46rem] text-primary-200 opacity-10" />
      </div>

      <div className="relative z-10 max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <div className="mx-auto h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
            <KeyRound className="h-7 w-7 text-white" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-semibold text-gray-900">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            We'll send a 6-digit OTP to your email
          </p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                i < stepIndex ? 'bg-primary-600 text-white' :
                i === stepIndex ? 'bg-primary-600 text-white ring-4 ring-primary-200' :
                'bg-gray-200 text-gray-500'
              }`}>
                {i < stepIndex ? '✓' : i + 1}
              </div>
              {i < steps.length - 1 && (
                <div className={`h-1 w-12 rounded transition-colors ${i < stepIndex ? 'bg-primary-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Card */}
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg py-8 px-6 sm:px-10">

          {/* Step 1: Email */}
          {step === 'email' && (
            <form onSubmit={handleSendOtp} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organisation</label>
                <input
                  type="text"
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  className="input"
                  placeholder="e.g. companyA"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input pl-10"
                    placeholder="your.email@company.com"
                    required
                  />
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full">
                {loading ? 'Sending OTP…' : 'Send OTP'}
              </button>
            </form>
          )}

          {/* Step 2: OTP */}
          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <p className="text-sm text-gray-600 text-center">
                Enter the 6-digit OTP sent to <span className="font-semibold text-gray-900">{email}</span>
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">OTP Code</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyRound className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className="input pl-10 tracking-widest text-center text-xl font-bold"
                    placeholder="123456"
                    maxLength={6}
                    required
                  />
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full">
                {loading ? 'Verifying…' : 'Verify OTP'}
              </button>
              <button
                type="button"
                onClick={handleSendOtp}
                disabled={loading}
                className="w-full text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Didn't receive it? Resend OTP
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input pl-10"
                    placeholder="Min. 6 characters"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input pl-10"
                    placeholder="Repeat your new password"
                    required
                  />
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full">
                {loading ? 'Resetting…' : 'Reset Password'}
              </button>
            </form>
          )}
        </div>

        {/* Back link */}
        <div className="text-center">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            {step === 'email' ? 'Back to Login' : 'Back'}
          </button>
        </div>
      </div>
    </div>
  );
}
